import { createSlice } from '@reduxjs/toolkit';
import ApiServices from 'api/apexchat.api.services';
import { CallConnectAPI, LeadAPI } from 'api/endpoints';
import mapSendCallConnectData from 'api/map-api-data/mapSendCallConnectData';
import mapLeadFormData from 'api/map-api-data/mapSendLeadFormData';
import storage from 'helpers/storage';
import {
  getPlainStringFromHTMLElementAsString,
  validateEmailFromSentence,
  validatePhoneFromSentence
} from 'helpers/validate';

/**
 * @name @createSlice
 * @description create redux store slice for auth
 **/
let leadFormInitialState = {
  name: '',
  email: '',
  phoneNumber: '',
  phoneNumberFrom: '',
  reason: '',
  type: 'Sales',
  categoryId: null,
  regionId: null,
  city: '',
  state: '',
  zip: ''
};
export const leadSlice = createSlice({
  name: 'lead',
  initialState: {
    lead: [],
    leadForm: {
      ...leadFormInitialState
    },
    isPhoneNumberFromFieldChange: false
  },

  reducers: {
    /**
     * @name @login
     * @description login reducer to call login async API with axios instance
     * @requires ApiServices.get method, state and API payload
     **/
    setLeadToState: (state, action) => {
      state.lead = [...state.lead, action.payload.data];
    },

    updateLeadToState: (state, action) => {
      // console.log(action.payload.data);
      if (action.payload.data.id) {
        let leads = [...state.lead];
        const index = leads.findIndex(
          lead => lead.id === action.payload.data.id
        );
        state.lead[index] = action.payload.data;
      }
    },

    removeLeadFromState: (state, action) => {
      let lead = [...state.lead];
      let index = lead.findIndex(com => com.id === action.payload.id);
      lead.splice(index, 1);
      state.lead = lead;
    },

    removeAllLeadFromState: (state, action) => {
      state.lead = [];
    },

    setIsPhoneNumberFromFieldChange: (state, action) => {
      state.isPhoneNumberFromFieldChange = action.payload;
    },

    /**
     * @name @setLeadFormUserCredentials
     * @description check and save email and phone number throughout the chat
     *              and update the state with latest
     * @requires message text
     * @result update/save the lead form state
     * */
    setLeadFormUserCredentials: (state, action) => {
      Object.keys(state.leadForm).forEach(key => {
        if (typeof action.payload !== 'string' && key in action.payload) {
          state.leadForm[key] = action.payload[key];
        }
      });

      let validateEmail = validateEmailFromSentence(
        getPlainStringFromHTMLElementAsString(action.payload)
      );
      if (validateEmail?.hasEmailAddress) {
        state.leadForm.email = validateEmail.email;
      }
      let validatePhoneNumber = validatePhoneFromSentence(action.payload);
      if (validatePhoneNumber?.hasPhoneNumber) {
        let country = JSON.parse(storage.getItem('MD_visitorCountry'));
        if (country && country?.dial_code) {
          state.leadForm.phoneNumber = `${country?.dial_code}-${validatePhoneNumber.phoneNumber}`;
        } else {
          state.leadForm.phoneNumber = validatePhoneNumber.phoneNumber;
        }
      }
    },

    /**
     * @name @setLeadFormReason
     * @description check and save visitor messages throughout the chat
     *              and update the state with latest
     * @requires message text
     * @result update/save the lead form reason state
     * */
    setLeadFormReason: (state, action) => {
      let msg = getPlainStringFromHTMLElementAsString(action.payload);
      let tempReason = state.leadForm.reason;
      tempReason = tempReason ? tempReason + '\n' + msg : msg;
      state.leadForm.reason = tempReason;
    },

    /**
     * @name @setLeadFormReasonBulk
     * @description set all payload in reason state and replace previous data if exist
     * @requires messages text
     * @result update/save the lead form reason state
     * */
    setLeadFormReasonBulk: (state, action) => {
      state.leadForm.reason = action.payload;
    },
    resetLeadFormReason: (state, action) => {
      state.leadForm.reason = '';
    },

    /**
     * @name @resetLeadFormUserCredentials
     * @description reset the lead form state
     * @requires null
     * @result set lead form email and phone to null
     * */
    resetLeadFormUserCredentials: state => {
      state.leadForm = {
        ...leadFormInitialState
      };
      state.isPhoneNumberFromFieldChange = false;
    }
  }
});

export const {
  setLeadToState,
  removeLeadFromState,
  removeAllLeadFromState,
  updateLeadToState,
  setLeadFormUserCredentials,
  setLeadFormReason,
  setLeadFormReasonBulk,
  resetLeadFormReason,
  resetLeadFormUserCredentials,
  setIsPhoneNumberFromFieldChange
} = leadSlice.actions;

/**
 * These function called thunk and allows to perform async logic
 * It can be dispatched like a regular action: `dispatch(data)`
 **/
export const getLeadAsync = payload => (dispatch, getState) => {
  let leadAPI = LeadAPI.get + '/' + payload.params.id;
  return ApiServices.getWithParams(leadAPI, payload)
    .then(async res => {
      let { data } = res;
      if (data?.success && data?.data) {
        await dispatch(setLeadToState(data));
        return Promise.resolve(data);
      }
      return Promise.reject(res);
    })
    .catch(error => {
      return Promise.reject(error);
    });
  // }
};
/* called a selector and allows to select values from the state */
export const selectLeads = state => {
  return state.lead;
};

/* called a selector and allows to select isPhoneNumberFromFieldChange from the state */
export const selectIsPhoneNumberFromFieldChange = state => {
  return state.leads.isPhoneNumberFromFieldChange;
};

export const selectLead = (state, id) => {
  let lead = state.leads.lead;
  if (id && lead.length) {
    let temp = lead.filter(c => c.id === id);

    if (temp.length) {
      return { lead: temp[0] };
    } else {
      return { lead: {} };
    }
  } else {
    return { lead: {} };
  }
};

export const getAllLeads = payload => {
  let leadAPI = LeadAPI.getAll;
  return ApiServices.getWithParams(leadAPI, { params: payload });
};

export const exportAllLeads = payload => {
  let leadAPI = LeadAPI.export;
  return ApiServices.WCF_getWithParams(leadAPI, { params: payload });
};

export const updateLead = payload => {
  let leadAPI = `${LeadAPI.put}/${payload.leadId}`;
  return ApiServices.put(leadAPI, payload);
};
/**
 * @name @sendLead
 * @description Send lead to the server from this action
 * @requires object of the lead form
 * @returns Promise
 * */
export const sendLead = payload => () => {
  return ApiServices.WCF_post(LeadAPI.post, mapLeadFormData(payload))
    .then(async ({ data }) => {
      if (data?.success && data?.data) {
        return Promise.resolve(data);
      }
      return Promise.reject(data?.error);
    })
    .catch(error => {
      return Promise.reject(error);
    });
};
/**
 * @name @sendLead
 * @description Send lead to the server from this action
 * @requires object of the lead form
 * @returns Promise
 * */
export const sendCallConnect = payload => () => {
  return ApiServices.WCF_post(
    CallConnectAPI.post(mapSendCallConnectData(payload)),
    mapSendCallConnectData(payload)
  )
    .then(async ({ data }) => {
      if (data?.success && data?.data) {
        return Promise.resolve(data);
      }
      return Promise.reject(data?.error);
    })
    .catch(error => {
      return Promise.reject(error);
    });
};
export const getLeadFormUserCredentials = state => {
  return state.leads.leadForm;
};

// fetch lead categories by company id
export const getLeadCategoriesByCompanyAsync = companyId => async dispatch => {
  return ApiServices.WCF_get(LeadAPI.getLeadCategories(companyId))
    .then(async response => {
      if (response.data.success && response.data.data) {
        return Promise.resolve(response.data.data);
      }
      return Promise.reject(false);
    })
    .catch(async err => {
      return Promise.reject('Error:' + err);
    });
};

// fetch regions by company id
export const getRegionsByCompanyAsync = companyId => async dispatch => {
  return ApiServices.WCF_get(LeadAPI.getRegions(companyId))
    .then(async response => {
      if (response.data.success && response.data.data) {
        return Promise.resolve(response.data.data);
      }
      return Promise.reject(false);
    })
    .catch(async err => {
      return Promise.reject('Error:' + err);
    });
};

/**
 * @name @forwardLead
 * @description forward lead to the users from this action
 * @requires object of the lead form
 * @returns Promise
 * */
export const onForwardLead = payload => {
  let url = LeadAPI.forwardlead(payload);
  return ApiServices.WCF_post(url, { notes: payload?.additional_notes })
    .then(async ({ data }) => {
      if (data?.success && data?.data) {
        return Promise.resolve(data);
      }
      return Promise.reject(data?.error);
    })
    .catch(error => {
      return Promise.reject(error);
    });
};

/**
 * @name @changeRequestLeadType
 * @description request lead type change
 * @requires Object of the lead type change form
 * @returns Promise
 * */
export const changeRequestLeadType = payload => {
  return ApiServices.WCF_post(LeadAPI.changeRequestLeadType(payload))
    .then(async ({ data }) => {
      if (data?.success && data?.data) {
        return Promise.resolve(data);
      }
      return Promise.reject(data?.error);
    })
    .catch(error => {
      return Promise.reject(error);
    });
};

export default leadSlice.reducer;
